import React, { useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { getAllClients } from "../../Redux/Actions/auth";
import { eventType } from "../../Utils/commonFiles";
import {
  createEvent,
  editEvent,
  getClientsEvents,
  getEventDetail,
  getEvents,
  getMusician,
} from "../../Redux/Actions/event";
import { toast } from "react-toastify";
import { presetsAllAction } from "../../Redux/Actions/presets";
import Select from "react-select";

const CreateEventModal = ({ id, show, handleClose, selectedEvent, disable = false }) => {
  const dispatch = useDispatch();
  const getInfo = useSelector((e) => e.userAuth.adminDetail);
  const visibleRange = useSelector((state) => state.eventAuth.visibleRange);
  const MusicianList = useSelector((e) => e.eventAuth.MusiciansList);

  const toAmount = (value) => Number(value) || 0;
  const getOptionalFeeSchema = (label) =>
    Yup.number()
      .transform((value, originalValue) => originalValue === "" ? 0 : value)
      .typeError(`${label} must be a number`)
      .min(0, `${label} cannot be negative`)
      .max(10000, `${label} cannot exceed 10000`);

  const validationSchema = Yup.object({
    client: Yup.string().required("Client is required"),
    // date: Yup.date().required("Date is required"),
    date: Yup.date()
      .required("Date is required")
      .min(new Date().toISOString().split("T")[0], "Enter a valid year")
      .test("reasonable-year", "Year must be a 4-digit number", (value) => {
        if (!value) return false; // Should not allow null or empty value
        const year = new Date(value).getFullYear();
        return year >= 1900 && year <= 2100; // Define your range
      }),
    startTime: Yup.string().required("Start time is required"),
    endTime: Yup.string().required("End time is required"),
    eventName: Yup.string().required("Package is required"),
    location: Yup.string().required("Location is required"),
    reference: Yup.string()
      .required("Booking reference is required")
      .matches(/^[A-Z0-9]+$/, "Only uppercase letters and numbers are allowed"),
    musician: Yup.string().required("Musician is required"),
    // price: Yup.number()
    //   .typeError("Price must be a number")
    //   .positive("Price must be positive")
    //   .required("Price is required"),
    bookingfee: Yup.number()
      .typeError("Booking Fee must be a number")
      .positive("Booking Fee must be positive")
      .max(10000, "Booking Fee cannot exceed 10000")
      .min(200, "Booking Fee must be at least 200.")
      .required("Booking Fee is required"),
    travelFee: getOptionalFeeSchema("Travel Fee"),
    songRequestFee: getOptionalFeeSchema("Song Request Fee"),
    cashDiscount: getOptionalFeeSchema("Cash Discount").test(
      "within-final-balance",
      "Cash Discount cannot be greater than the final balance",
      function (value) {
        const bookingFee = toAmount(this.parent.bookingfee);
        const travelFee = toAmount(this.parent.travelFee);
        const songRequestFee = toAmount(this.parent.songRequestFee);
        const cashDiscount = toAmount(value);
        const finalBalance = bookingFee + travelFee + songRequestFee - 100;

        return cashDiscount <= Math.max(finalBalance, 0);
      }
    ),

    // otherExpense: Yup.number()
    //   .typeError("Other Expense must be a number")
    //   .positive("Other Expense must be positive")
    //   .max(10000, "Other Expense cannot exceed 10000")
    //   .min(200, "Other Expense must be at least 200.")
    //   .required("Other Expense is required"),
  });

  const getEventTypeKey = (label) => {
    return Object.keys(eventType).find((key) => eventType[key] === label) || "";
  };

  const formik = useFormik({
    initialValues: {
      client: selectedEvent?.client?._id || selectedEvent?.client || "",
      date: selectedEvent?.eventDate?.split("T")[0] || "",
      startTime:
        selectedEvent?.eventTime?.from?.split("T")[1]?.slice(0, 5) || "",
      endTime: selectedEvent?.eventTime?.to?.split("T")[1]?.slice(0, 5) || "",
      eventName: selectedEvent?.eventName || "",
      location: selectedEvent?.location || "",
      // musician: selectedEvent?.musician || "",
      musician: selectedEvent?.musician?._id || "",
      reference: selectedEvent?.reference || "",
      price: selectedEvent?.price || "",
      bookingfee: selectedEvent?.bookingfee || "",
      travelFee: selectedEvent?.travelFee ?? selectedEvent?.otherExpense ?? "",
      songRequestFee: selectedEvent?.songRequestFee ?? "",
      cashDiscount: selectedEvent?.cashDiscount ?? "",
      eventType: selectedEvent?.eventType?._id || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const bookingFee = parseFloat(values.bookingfee) || 0;
      const travelFee = parseFloat(values.travelFee) || 0;
      const songRequestFee = parseFloat(values.songRequestFee) || 0;
      const cashDiscount = parseFloat(values.cashDiscount) || 0;
      const otherExpense = travelFee + songRequestFee;
      const totalPrice = bookingFee + otherExpense - cashDiscount;
      const formattedData = {
        client: values.client,
        eventDate: values.date,
        eventTime: {
          from: new Date(
            `${values.date}T${values.startTime}:00Z`
          ).toISOString(),
          to: new Date(`${values.date}T${values.endTime}:00Z`).toISOString(),
        },
        eventType: values?.eventType,
        eventName: values.eventName,
        location: values.location,
        reference: values.reference,
        musician: values.musician,
        price: totalPrice,
        bookingfee: bookingFee,
        otherExpense,
        travelFee,
        songRequestFee,
        cashDiscount,
        isDiscounted: cashDiscount > 0,
      };
      if (selectedEvent?._id) {
        dispatch(editEvent({ id: selectedEvent._id, ...formattedData })).then(
          (res) => {
            if (res?.payload?.statusCode === 200) {
              toast.success("Event Updated Successfully");
              dispatch(getEventDetail({ id: selectedEvent._id }));
              if (id) {
                dispatch(getClientsEvents({ page: 0, limit: 5, clientId: id }));
              }
              formik.resetForm();
              handleClose();
            } else {
              toast.error(res?.payload?.message);
            }
          }
        );
      } else {
        dispatch(createEvent(formattedData)).then((res) => {
          if (res?.payload?.statusCode === 200) {
            toast.success("Event Created Successfully");
            if (id) {
              dispatch(getClientsEvents({ page: 0, limit: 5, clientId: id }));
            }
            if (visibleRange) {
              dispatch(
                getEvents({
                  startOfMonth: visibleRange.start,
                  endOfMonth: visibleRange.end,
                })
              );
            }
            formik.resetForm();
            handleClose();
          } else {
            toast.error(res?.payload?.message);
          }
        });
      }
    },
  });

  const getClientsList = useSelector((e) => e.userAuth.clientList);
  const listAllEvent = useSelector((e) => e.presetAuth.presetAllList);
  const getAllClientsList = useSelector((e) => e.userAuth.allClientList);

  useEffect(() => {
    dispatch(getAllClients());
    dispatch(presetsAllAction());
    dispatch(getMusician());
  }, [dispatch]);

  useEffect(() => {
    if (id && show) {
      formik.setFieldValue("client", id);
    }
  }, [id, show]);

  const customStyles = {
    singleValue: (provided) => ({
      ...provided,
      color: "black",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "grey",
    }),
  };

  const handleFeeBlur = (e, fieldName) => {
    if (!e.target.value || e.target.value.trim() === "") {
      formik.setFieldValue(fieldName, 0);
    }
    formik.handleBlur(e);
  };

  const finalBalance =
    toAmount(formik.values.bookingfee) +
    toAmount(formik.values.travelFee) +
    toAmount(formik.values.songRequestFee) -
    toAmount(formik.values.cashDiscount) -
    100;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="border-none justify-content-center">
          <Modal.Title className="mt-3">
            {selectedEvent?._id ? "Update Event" : "Create Event"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-0">
          <Form onSubmit={formik.handleSubmit}>
            <div className="modal-cmn-form-set">
              {/* Client */}
              <Form.Group className="mb-3" controlId="client">
                <Form.Label>Client</Form.Label>
                <Select
                  styles={customStyles}
                  id="category"
                  className=""
                  name="client"
                    isDisabled={disable}
                  options={getAllClientsList?.map((elem) => ({
                    value: elem?._id,
                    label: elem?.fullName,
                  }))}
                  value={getAllClientsList
                    ?.map((elem) => ({
                      value: elem?._id,
                      label: elem?.fullName,
                    }))
                    .find((option) => option.value === formik.values.client)}
                  onChange={(selectedOption) =>
                    formik.setFieldValue("client", selectedOption?.value || "")
                  }
                  onBlur={() => formik.setFieldTouched("client", true)}
                  placeholder="Select Client"
                />
                {formik.touched.client && formik.errors.client && (
                  <div className="text-danger">{formik.errors.client}</div>
                )}
              </Form.Group>

              {/* Date */}
              <Form.Group className="mb-3" controlId="date">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  className={
                    formik.values.date === ""
                      ? "placeholder-select"
                      : "filled-select"
                  }
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min={new Date().toISOString().split("T")[0]}
                />
                {formik.touched.date && formik.errors.date && (
                  <div className="text-danger">{formik.errors.date}</div>
                )}
              </Form.Group>

              {/* Time */}
              <Form.Group className="mb-3" controlId="time">
                <Form.Label>Time</Form.Label>
                <div className="d-flex align-items-center gap-3">
                  <Form.Control
                    type="time"
                    name="startTime"
                    value={formik.values.startTime}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <p className="m-0">To</p>
                  <Form.Control
                    type="time"
                    name="endTime"
                    value={formik.values.endTime}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.startTime && formik.errors.startTime && (
                  <div className="text-danger">{formik.errors.startTime}</div>
                )}
                {formik.touched.endTime && formik.errors.endTime && (
                  <div className="text-danger">{formik.errors.endTime}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="eventName">
                <Form.Label>Package</Form.Label>
                <Form.Control
                  type="text"
                  name="eventName"
                  value={formik.values.eventName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter name"
                />
                {formik.touched.eventName && formik.errors.eventName && (
                  <div className="text-danger">{formik.errors.eventName}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="eventType">
                <Form.Label>Event Type</Form.Label>
                <Form.Select
                  id="category"
                  name="eventType"
                  className={
                    formik.values.eventType === ""
                      ? "placeholder-select"
                      : "filled-select"
                  }
                  value={formik.values.eventType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const selectedOption = e.target.selectedOptions[0];
                      if (selectedOption) {
                        formik.setFieldValue("eventType", selectedOption.value);
                      }
                    }
                  }}
                >
                  <option value="">Select Event Type</option>
                  {listAllEvent?.map((elem, i) => (
                    <option key={i} value={elem?._id}>
                      {elem?.eventType}
                    </option>
                  ))}
                </Form.Select>
                {formik.touched.eventType && formik.errors.eventType && (
                  <div className="text-danger">{formik.errors.eventType}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="location">
                <Form.Label>Venue</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Type here"
                />
                {formik.touched.location && formik.errors.location && (
                  <div className="text-danger">{formik.errors.location}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="location">
                <Form.Label>Booking Reference</Form.Label>
                <Form.Control
                  type="text"
                  name="reference"
                  value={formik.values.reference}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter reference"
                />
                {formik.touched.reference && formik.errors.reference && (
                  <div className="text-danger">{formik.errors.reference}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="musician">
                <Form.Label>Musician</Form.Label>
                <Form.Select
                  id="category"
                  name="musician"
                  className={
                    formik.values.musician == ""
                      ? "placeholder-select"
                      : "filled-select"
                  }
                  value={formik.values.musician}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const selectedOption = e.target.selectedOptions[0];
                      if (selectedOption) {
                        formik.setFieldValue("musician", selectedOption.value); // Update Formik value
                      }
                    }
                  }}
                >
                  <option value="">Select Musician</option>
                  {MusicianList?.map((elem) => (
                    <option value={elem?._id}>{elem?.fullName}</option>
                  ))}
                </Form.Select>
                {formik.touched.musician && formik.errors.musician && (
                  <div className="text-danger">{formik.errors.musician}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="price">
                <Form.Label>Booking Fee</Form.Label>
                <div className="ad-dollor">
                  <span className="input-prefix">£</span>
                  <Form.Control
                    type="text"
                    name="bookingfee"
                    value={formik.values.bookingfee}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder=" Enter booking fee"
                  />
                </div>
                {formik.touched.bookingfee && formik.errors.bookingfee && (
                  <div className="text-danger">{formik.errors.bookingfee}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="travelFee">
                <Form.Label>Travel Fee</Form.Label>
                <div className="ad-dollor">
                  <span className="input-prefix">£</span>
                  <Form.Control
                    type="text"
                    name="travelFee"
                    value={formik.values.travelFee}
                    onChange={formik.handleChange}
                    onBlur={(e) => handleFeeBlur(e, "travelFee")}
                    placeholder=" Enter travel fee"
                  />
                </div>
                {formik.touched.travelFee && formik.errors.travelFee && (
                  <div className="text-danger">{formik.errors.travelFee}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="songRequestFee">
                <Form.Label>Song Request Fee</Form.Label>
                <div className="ad-dollor">
                  <span className="input-prefix">£</span>
                  <Form.Control
                    type="text"
                    name="songRequestFee"
                    value={formik.values.songRequestFee}
                    onChange={formik.handleChange}
                    onBlur={(e) => handleFeeBlur(e, "songRequestFee")}
                    placeholder=" Enter song request fee"
                  />
                </div>
                {formik.touched.songRequestFee && formik.errors.songRequestFee && (
                  <div className="text-danger">{formik.errors.songRequestFee}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="cashDiscount">
                <Form.Label>Cash Discount</Form.Label>
                <div className="ad-dollor">
                  <span className="input-prefix">£</span>
                  <Form.Control
                    type="text"
                    name="cashDiscount"
                    value={formik.values.cashDiscount}
                    onChange={formik.handleChange}
                    onBlur={(e) => handleFeeBlur(e, "cashDiscount")}
                    placeholder=" Enter cash discount"
                  />
                </div>
                {formik.touched.cashDiscount && formik.errors.cashDiscount && (
                  <div className="text-danger">{formik.errors.cashDiscount}</div>
                )}
              </Form.Group>

              <div className="event-final-balance-preview">
                <span>Final Balance</span>
                <strong>£{Math.max(finalBalance, 0).toFixed(2)}</strong>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-none justify-content-center">
          <button className="secondary" onClick={handleClose}>
            Close
          </button>
          <button
            type="submit"
            className="primary"
            onClick={formik.handleSubmit}
          >
            {selectedEvent?._id ? "Update" : "Create"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateEventModal;
